import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { useEffect, useRef, useState } from 'react';
import { Alert, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  fetchHumanRemovalPreview,
  removeGuildMember,
  removeHumanGuildMemberCascade,
  type HumanRemovalPreview,
} from '../lib/api';
import { routes } from '../lib/routes';
import { useAuthStore, useServerStore } from '../stores';

interface Props {
  userId: string;
  displayName: string;
  role: 'member' | 'agent';
  canRemove: boolean;
  compact?: boolean;
  onDialogOpenChange?: (open: boolean) => void;
}

// The caller derives the current viewer's authority from the canonical roster;
// the server remains authoritative for the destructive command.
export default function ServerMemberRemovalControl({
  userId,
  displayName,
  role,
  canRemove,
  compact = false,
  onDialogOpenChange,
}: Props) {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.currentUser);
  const guildId = useAuthStore((state) => state.guildId);
  const reconcileMembership = useServerStore((state) => state.reconcileMembership);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<HumanRemovalPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<MessageDescriptor | null>(null);
  const generation = useRef(0);
  const removalInFlight = useRef(false);

  useEffect(() => {
    if (canRemove) return;
    generation.current++;
    setOpen(false);
    setPreview(null);
    setError(null);
  }, [canRemove]);

  useEffect(() => {
    generation.current++;
    setOpen(false);
    setPreview(null);
    setError(null);
  }, [guildId, userId]);

  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );

  if (!guildId || !canRemove || userId === currentUser?.id) return null;

  const openConfirmation = () => {
    const request = ++generation.current;
    onDialogOpenChange?.(true);
    setOpen(true);
    setPreview(null);
    setError(null);
    if (role !== 'member') return;
    setPreviewLoading(true);
    void fetchHumanRemovalPreview(guildId, userId)
      .then((result) => {
        if (request === generation.current) setPreview(result);
      })
      .catch(() => {
        if (request === generation.current) setError(msg`Could not preview removal.`);
      })
      .finally(() => {
        if (request === generation.current) setPreviewLoading(false);
      });
  };

  const remove = async () => {
    if (removalInFlight.current) return;
    removalInFlight.current = true;
    const request = ++generation.current;
    const accountId = currentUser?.id;
    try {
      setRemoving(true);
      if (role === 'member') await removeHumanGuildMemberCascade(guildId, userId);
      else await removeGuildMember(guildId, userId);
      const currentAuth = useAuthStore.getState();
      if (
        request !== generation.current ||
        currentAuth.guildId !== guildId ||
        currentAuth.currentUser?.id !== accountId
      )
        return;
      if (request !== generation.current) return;
      setOpen(false);
      onDialogOpenChange?.(false);
      navigate(routes.members(guildId));
      void reconcileMembership(guildId).catch(() => undefined);
    } catch {
      if (request === generation.current) setError(msg`Could not remove member.`);
    } finally {
      removalInFlight.current = false;
      if (request === generation.current) setRemoving(false);
    }
  };

  return (
    <>
      {compact ? (
        <button
          type="button"
          title={t`Remove ${displayName} from Project`}
          aria-label={t`Remove ${displayName} from Project`}
          onClick={openConfirmation}
          role="menuitem"
        >
          <Trans>Remove from Project</Trans>
        </button>
      ) : (
        <div className="agent-profile-field">
          <div className="agent-profile-field-label">
            <Trans>PROJECT MEMBERSHIP</Trans>
          </div>
          <button type="button" className="btn btn-sm btn-danger" onClick={openConfirmation}>
            <Trans>Remove from Project</Trans>
          </button>
        </div>
      )}
      <Modal
        open={open}
        title={t`Remove ${displayName} from this Project?`}
        okText={role === 'member' ? t`Remove Human and access` : t`Remove Agent`}
        cancelText={t`Cancel`}
        okButtonProps={{
          danger: true,
          disabled: removing || (role === 'member' && (!preview || previewLoading)),
        }}
        confirmLoading={removing}
        cancelButtonProps={{ disabled: removing }}
        closable={!removing}
        keyboard={!removing}
        mask={{ closable: !removing }}
        onOk={() => void remove()}
        onCancel={() => {
          generation.current++;
          setOpen(false);
          onDialogOpenChange?.(false);
        }}
        destroyOnHidden
        width={520}
      >
        {role === 'agent' ? (
          <p>
            <Trans>This removes only this Agent's Project membership.</Trans>
          </p>
        ) : previewLoading ? (
          <p>
            <Trans>Loading removal impact…</Trans>
          </p>
        ) : preview ? (
          <>
            <p>
              <Trans>This also removes this Human's directly owned Agents from this Project.</Trans>
            </p>
            <ul>
              {preview.agents.map((agent) => (
                <li key={agent.user_id}>
                  <strong>{agent.display_name}</strong>:{' '}
                  {agent.channel_names.length
                    ? agent.channel_names.join(', ')
                    : t`no channel memberships`}
                </li>
              ))}
            </ul>
            <p>
              <Trans>
                <Plural
                  value={preview.channel_count}
                  one="# channel membership"
                  other="# channel memberships"
                />{' '}
                and{' '}
                <Plural
                  value={preview.thread_count}
                  one="# thread membership"
                  other="# thread memberships"
                />{' '}
                will be removed.
              </Trans>
            </p>
          </>
        ) : (
          <p>
            <Trans>Removal impact is unavailable.</Trans>
          </p>
        )}
        {error && <Alert type="error" showIcon message={i18n._(error)} role="alert" />}
      </Modal>
    </>
  );
}
